import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  Editor,
  NgxEditorComponent,
  NgxEditorMenuComponent,
  type Toolbar,
} from 'ngx-editor';
import { MarkdownParser } from '@core/utils/markdown-parser';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { MindmapTranslations } from '@/app/views/mindmap/mindmap-translations';
import { noteEditorSchema } from './note-editor-schema';

export type MapNoteModalMode = 'popup' | 'page';

export type NoteDraftChangedEvent = {
  nodeId: string | null;
  text: string;
};

export type NoteFlushRequestReason = 'close' | 'node-change' | 'destroy';

export type NoteFlushRequestedEvent = {
  nodeId: string | null;
  reason: NoteFlushRequestReason;
};

@Component({
  selector: 'app-map-note-modal-view',
  imports: [ReactiveFormsModule, NgxEditorComponent, NgxEditorMenuComponent],
  templateUrl: './map-note-modal.html',
  styleUrl: './map-note-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapNoteModalViewComponent implements OnInit, OnDestroy {
  private readonly uiTranslation = inject(UiTranslationService);

  readonly mode = input<MapNoteModalMode>('popup');
  readonly isOpen = input(false);
  readonly noteText = input('');
  readonly noteSubtitle = input('');
  readonly activeNoteNodeId = input<string | null>(null);
  readonly closeOnBackdrop = input(true);
  readonly noteDraftChanged = output<NoteDraftChangedEvent>();
  readonly noteFlushRequested = output<NoteFlushRequestedEvent>();
  readonly closeRequested = output<void>();
  protected readonly i18n =
    this.uiTranslation.namespace<MindmapTranslations>('mindmap');

  protected readonly isPopupMode = computed(() => this.mode() === 'popup');
  protected readonly editor = new Editor({
    schema: noteEditorSchema,
    parseOptions: { preserveWhitespace: 'full' },
  });
  protected readonly toolbar: Toolbar = [
    ['bold', 'italic', 'code', 'underline'],
    ['ordered_list', 'bullet_list', { heading: ['h1', 'h2', 'h3'] }],
    ['text_color', 'background_color'],    
    ['horizontal_rule', 'format_clear', 'indent', 'outdent'],
  ];
  protected readonly control = new FormControl<string>('', {
    nonNullable: true,
  });
  protected readonly isFullscreen = signal(false);
  /**
   * The template binds this string through Angular's standard innerHTML
   * sanitizer. Never mark note or mind-map content as trusted HTML.
   */
  protected readonly subtitleHtml = computed(() =>
    MarkdownParser.parse(this.noteSubtitle() ?? ''),
  );
  private readonly subscriptions = new Subscription();
  private lastActiveNoteNodeId: string | null = null;
  private hasObservedActiveNode = false;

  constructor() {
    effect(() => {
      const value = this.normalizeNoteText(this.noteText() ?? '');
      if (this.control.value !== value) {
        this.control.setValue(value, { emitEvent: false });
      }
    });

    effect(() => {
      if (this.isPopupMode() && !this.isOpen()) {
        this.isFullscreen.set(false);
      }
    });

    effect(() => {
      const activeNodeId = this.activeNoteNodeId() ?? null;
      if (!this.hasObservedActiveNode) {
        this.lastActiveNoteNodeId = activeNodeId;
        this.hasObservedActiveNode = true;
        return;
      }

      if (activeNodeId === this.lastActiveNoteNodeId) {
        return;
      }

      const previousNodeId = this.lastActiveNoteNodeId;
      this.lastActiveNoteNodeId = activeNodeId;
      if (previousNodeId) {
        this.noteFlushRequested.emit({
          nodeId: previousNodeId,
          reason: 'node-change',
        });
      }
    });
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.control.valueChanges.subscribe((value) => {
        const normalized = this.normalizeOutgoingNoteText(value ?? '');
        const nodeId = this.activeNoteNodeId() ?? null;
        this.noteDraftChanged.emit({ nodeId, text: normalized });
      }),
    );
  }

  ngOnDestroy(): void {
    this.requestFlush('destroy');
    this.subscriptions.unsubscribe();
    this.editor.destroy();
  }

  onBackdropClick(): void {
    if (!this.isPopupMode() || !this.closeOnBackdrop()) {
      return;
    }

    this.requestFlush('close');
    this.closeRequested.emit();
  }

  onCloseClick(): void {
    this.requestFlush('close');
    this.closeRequested.emit();
  }

  onToggleFullscreenClick(): void {
    this.isFullscreen.update((value) => !value);
  }

  private normalizeNoteText(value: string): string {
    if (this.looksLikeHtml(value)) {
      return value;
    }
    return this.preserveHtmlWhitespace(MarkdownParser.parse(value));
  }

  private looksLikeHtml(value: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(value);
  }

  private normalizeOutgoingNoteText(value: string): string {
    if (!this.looksLikeHtml(value)) {
      return value;
    }
    return this.preserveHtmlWhitespace(value);
  }

  private preserveHtmlWhitespace(value: string): string {
    if (!value) {
      return value;
    }

    const nbsp = '\u00a0';
    return value.replace(/>([^<]+)</g, (_match, text: string) => {
      const withExtraSpaces = text.replace(/ {2,}/g, (spaces: string) => {
        return ` ${nbsp.repeat(spaces.length - 1)}`;
      });
      const withTrailingSpaces = withExtraSpaces.replace(/ $/g, nbsp);
      return `>${withTrailingSpaces}<`;
    });
  }

  private requestFlush(reason: NoteFlushRequestReason): void {
    const nodeId = this.activeNoteNodeId() ?? null;
    this.noteFlushRequested.emit({ nodeId, reason });
  }
}
